import axios from "axios";
import authHeader from "./authHeader";

// let BASE_URL = "https://api-staging.aiavantra.com/api/v1";
// let BASE_URL_PROD = "https://api.aiavantra.com/api/v1";

// Create Axios instance without Authorization initially
const client = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add interceptor to inject token dynamically into every request
client.interceptors.request.use(
  (config) => {
    if (config.skipAuth) {
      if (typeof config.headers?.delete === "function") {
        config.headers.delete("Authorization");
      } else if (config.headers) {
        delete config.headers.Authorization;
      }
      return config;
    }

    config.headers = {
      ...config.headers,
      ...authHeader(),
    };

    const userRole = localStorage.getItem("user_role");
    const isAdmin =
      userRole === "admin" ||
      userRole === "hospital_admin" ||
      userRole === "ADMIN";
    const selectedStaffId = sessionStorage.getItem("admin_selected_staff_id");

    if (
      isAdmin &&
      selectedStaffId &&
      !config.url?.includes("/hospital-admin/") &&
      !config.url?.includes("/admin-dashboard") &&
      !config.url?.includes("/hospital-mint/")
    ) {
      const method = config.method?.toLowerCase();
      const isSystemicPayloadFetch =
        method === "post" &&
        /\/documents\/super-admin\/systemic\/payload\//.test(config.url || "");

      if (method === "get") {
        config.params = {
          staffId: selectedStaffId,
          ...config.params,
        };
      } else if (!isSystemicPayloadFetch) {
        // Prevent mutation requests in admin viewing mode
        return Promise.reject({
          isBlockedByAdminReadOnly: true,
          message:
            "Admin read-only mode: Mutations are disabled when viewing stakeholder.",
        });
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Optional: method to change Content-Type for file uploads
export const setMultipart = (isMultipart = false) => {
  client.defaults.headers["Content-Type"] = isMultipart
    ? "multipart/form-data"
    : "application/json";
};

// Login API

const login = (data) => {
  setMultipart(false);
  return client.post("/auth/hospital-login", data);
};

const hospitalResetPassword = (data) => {
  setMultipart(false);
  return client.post("/auth/hospital-reset-password", data);
};

const requestPasswordReset = (email) => {
  setMultipart(false);
  return client.post(
    "/partner/auth/password-reset/request",
    { email },
    { skipAuth: true },
  );
};

const verifyPasswordReset = (data) => {
  setMultipart(false);
  return client.post("/partner/auth/password-reset/verify", data, {
    skipAuth: true,
  });
};

const confirmPasswordReset = (data) => {
  setMultipart(false);
  return client.post("/partner/auth/password-reset/confirm", data, {
    skipAuth: true,
  });
};

// Profile APIs
const getStaffMeProfile = () => {
  return client.get("/staff/me/profile");
};

const postClientIssueReport = (data) => {
  setMultipart(false);
  return client.post("/clients/me/issue-reports", data);
};

const getAvailability = (staffId) => {
  return client.get(`/staff/${staffId}/availability`);
};

const updateAvailability = (staffId, data) => {
  return client.put(`/staff/${staffId}/availability`, data);
};

// Upload Files APIs

const uploadSingleFile = (key, data) => {
  setMultipart(true);
  return client.post("/upload", data, { params: { fileKey: key } });
};

const uploadMultipleFiles = (key, data) => {
  setMultipart(true);
  return client.post("/upload/multiple", data, { params: { fileKey: key } });
};

const getArrayByKey = (key, params) => {
  return client.get(`/public/services/${key}`, { params });
};

// RECEPTIONIST APIs

const getOpdAppointments = (params) => {
  return client.get("/opd/appointments", { params });
};

const getOpdPatients = (params) => {
  return client.get("/opd/patients", { params });
};

const getReceptionistDashboard = (params) => {
  return client.get("/opd/dashboard/stats", { params });
};

const getReceptionistAppointmentBehavior = (params) => {
  return client.get("/opd/dashboard/appointment-behavior", { params });
};

const cancelAppointment = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/${id}/cancel`, data);
};

const markAsNoShowAppointment = (id) => {
  setMultipart(false);
  return client.patch(`/opd/${id}/no-show`);
};

const addPatient = (data) => {
  setMultipart(false);
  return client.post("/opd/patients", data);
};

const updatePatient = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/patient-details?patientId=${id}`, data);
};

const getStaffList = (params) => {
  return client.get(`/opd/staff-list`, { params });
};

const getStaffDetails = (id) => {
  return client.get(`/opd/assignee/${id}/slots`);
};

const getOpdReceptionistAvailableSlots = (params) => {
  return client.get(`/opd/receptionist/available-slots`, { params });
};

const getOpdAssigneeSlotTypePricing = (params) => {
  return client.get(`/opd/assignee/slot-type-pricing`, { params });
};

const addAppointment = (data) => {
  setMultipart(false);
  return client.post(`/opd`, data);
};

const createReceptionistOpdInvoice = (opdId, data) => {
  setMultipart(false);
  return client.post(`/opd/receptionist/${opdId}/invoice`, data);
};

const printReceptionistInvoice = (invoiceId, params) => {
  return client.get(`/opd/receptionist/invoices/${invoiceId}/print`, {
    params,
  });
};

const printCounsellorInvoice = (invoiceId, params) => {
  return client.get(`/opd/counsellor/invoices/${invoiceId}/print`, { params });
};

const getReceptionistPaymentMethods = () => {
  return client.get(`/opd/receptionist/payment-methods`);
};

const getCounsellorPaymentMethods = () => {
  return client.get(`/opd/counsellor/payment-methods`);
};

const createReceptionistOpdQrCode = (opdId, data) => {
  setMultipart(false);
  return client.post(`/opd/receptionist/${opdId}/qr-code`, data);
};

const getReceptionistOpdQrCodeStatus = (opdId, qrCodeId) => {
  return client.get(`/opd/receptionist/${opdId}/qr-code/${qrCodeId}/status`);
};

const createCounsellorInvoiceQrCode = (invoiceId) => {
  setMultipart(false);
  return client.post(`/opd/counsellor/invoices/${invoiceId}/qr-code`);
};

const getCounsellorInvoiceQrCodeStatus = (invoiceId, qrCodeId) => {
  return client.get(
    `/opd/counsellor/invoices/${invoiceId}/qr-code/${qrCodeId}/status`,
  );
};

const rescheduleAppointment = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/${id}/reschedule`, data);
};

const getCalendarSlots = (params) => {
  return client.get(`/opd/appointments/calendar`, { params });
};

const getBillings = (params) => {
  return client.get(`/opd/billing-list`, { params });
};

const getDoctorStatus = (params) => {
  return client.get(`/opd/doctor-status`, { params });
};

const getDoctorStaffSchedule = (params) => {
  return client.get(`/opd/doctor-staff/schedule`, { params });
};

const getAssessments = (params) => {
  return client.get(`/opd/pre-assessment-list`, { params });
};

const getAssessmentView = (opdId) => {
  return client.get(`doctor/opd/${opdId}/pre-assessment`);
};

const editAssessment = (opdId, data) => {
  setMultipart(false);
  return client.patch(`/opd/${opdId}/pre-assessment/`, data);
};

const getAssessmentStats = (params) => {
  return client.get(`/opd/pre-assessment-list/stats`, { params });
};

const getFollowUpList = (params) => {
  return client.get(`/opd/prescriptions/follow-up-management`, { params });
};

const getFollowUpStats = (params) => {
  return client.get(`/opd/prescriptions/follow-up-management/stats`, {
    params,
  });
};

const confirmFollowUp = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/prescriptions/${id}/follow-up-status`, data);
};

const cancelFollowUp = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/prescriptions/${id}/follow-up-status`, data);
};

const getFollowUpSlots = (id, params) => {
  return client.get(`/opd/assignee/${id}/slots`, { params });
};

const rescheduleFollowUp = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/${id}/reschedule`, data);
};

const getPatient = (params) => {
  return client.get(`/opd/patient-details`, { params });
};

const getPatientAppointments = (params) => {
  return client.get(`/opd/patient-history`, { params });
};

const getPatientInvoices = (params) => {
  return client.get(`/opd/patient-history/billing-details`, { params });
};

// Doctor APIs

const getDoctorDashboard = (params) => {
  return client.get(`/doctor/dashboard`, { params });
};

const getDoctorAppointments = (params) => {
  return client.get("/doctor/opd-list", { params });
};

const getDoctorPrescriptions = (params) => {
  return client.get("/opd/prescriptions", { params });
};

const getDoctorPrescription = (id) => {
  return client.get(`/opd/prescriptions/${id}`);
};

const listAvatarDraftPrescriptions = (params) => {
  return client.get("/avatar-draft-prescriptions", { params });
};

const getAvatarDraftPrescription = (draftId) => {
  return client.get(`/avatar-draft-prescriptions/${draftId}`);
};

const reviewAvatarDraftPrescription = (draftId, data) => {
  return client.post(`/avatar-draft-prescriptions/${draftId}/review`, data);
};

const saveAvatarDraftLineItems = (draftId, data) => {
  return client.patch(
    `/avatar-draft-prescriptions/${draftId}/line-items`,
    data,
  );
};

const followUpAvatarDraftPrescription = (draftId, data) => {
  return client.post(`/avatar-draft-prescriptions/${draftId}/follow-up`, data);
};

const printDoctorPrescription = (id, params = {}) => {
  return client.get(`/opd/prescriptions/${id}/print`, { params });
};

const getDoctorAppointmentCalendar = (params) => {
  return client.get("/doctor/appointments/calendar", { params });
};

const getDoctorAssessment = (id) => {
  return client.get(`/doctor/opd/${id}/pre-assessment`);
};

const getOpdAppointment = (id) => {
  return client.get(`/opd/${id}`);
};

const addDoctorOpdPrescription = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/${id}/prescription`, data);
};

const addDoctorIpdPrescription = (data) => {
  setMultipart(false);
  return client.post(`/opd/ipd-prescription`, data);
};

const editDoctorIpdPrescription = (id, data) => {
  setMultipart(false);
  return client.put(`/opd/ipd-prescription/${id}`, data);
};

const getAllProcedures = () => {
  return client.get(`/ipd-procedures/hospital/procedures`);
};

const financeDashboard = (params) => {
  return client.get(`/doctor/finance-dashboard`, { params });
};

const getRecentTransactions = (params) => {
  return client.get(`/doctor/finance-dashboard/recent-transactions`, {
    params,
  });
};

const productItemsList = (params) => {
  return client.get(`/doctor/catalog-items/products`, { params });
};

const serviceItemsList = (params) => {
  return client.get(`/doctor/catalog-items/services`, { params });
};

const getIpdPatients = (params) => {
  return client.get(`/doctor/ipd-users`, { params });
};

const getPatientDetails = (id) => {
  return client.get(`/ipd-users/${id}`);
};

const getPatientReports = (id, params) => {
  return client.get(`/ipd-users/${id}/test-reports/`, { params });
};

const getProcedureData = (id) => {
  return client.get(`/ipd-users/${id}/procedures`);
};

const getClinicalNotes = (id, params) => {
  return client.get(`/ipd-users/${id}/clinical-progress-notes/`, { params });
};

const addOrEditProcedure = (id, data) => {
  setMultipart(false);
  return client.put(`/ipd-users/${id}/procedures`, data);
};

const createDoctorNote = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/${id}/clinical-progress-notes`, data);
};

const createNurseNote = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/${id}/clinical-progress-notes`, data);
};

const getSnapshotData = (params) => {
  return client.get(`/user/digital-health-profile/snapshots`, { params });
};

const getDhpData = (params) => {
  return client.get(`/user/digital-health-profile/snapshot/details`, {
    params,
  });
};

const getDhpKeyVitals = (params) => {
  return client.get(`/user/digital-health-profile/key-vitals`, { params });
};

const getMesh = (params) => {
  return client.get(`/mesh/${params.meshname}`);
};

const getMeshPayload = (patientId, snapshotId) => {
  const params =
    snapshotId != null && String(snapshotId).trim() !== ""
      ? { snapshotId: String(snapshotId).trim() }
      : undefined;
  return client.post(
    `/documents/super-admin/systemic/payload/${patientId}`,
    undefined,
    params ? { params } : undefined,
  );
};

const getPatientSystemic = (patientId) => {
  return client.get(`/user-documents/systemic/patient/${patientId}`);
};

const getOrganInsights = (patientId) => {
  return client.get(`/user-documents/organ-insights/patient/${patientId}`);
};

const getDietList = (params) => {
  return client.get(`/diets`, { params });
};

const startDischargeFromDoctor = (id, data) => {
  return client.patch(`/doctor/ipd-users/${id}/status`, data);
};

//Counsellor APIs

const getCounsellorDashboard = (params) => {
  return client.get(`/opd/counsellor/dashboard`, { params });
};

const getCounsellorList = (params) => {
  return client.get(`/opd/counsellor`, { params });
};

const getPatientSummary = (id) => {
  return client.get(`/opd/counsellor/${id}/patient-summary`);
};

const cancelAdmission = (id, body) => {
  setMultipart(false);
  return client.put(`/ipd-users/${id}/cancel`, body);
};

const moveToIpdManager = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/${id}/status/ipd-management`, data);
};

const getItemInfo = (id) => {
  return client.get(`/ipd-users/ipd-manager/vendors/catalog-items/${id}`);
};

const createInvoice = (id, data) => {
  setMultipart(false);
  return client.post(`/opd/counsellor/${id}/invoice`, data);
};

const getCounsellorCatalogItemPricing = (itemId) => {
  return client.get(`/opd/counsellor/catalog-item/${itemId}/pricing`);
};

const getInvoiceList = (params) => {
  return client.get(`/opd/counsellor/invoices`, { params });
};

const getInvoiceById = (id) => {
  return client.get(`/opd/counsellor/invoices/${id}`);
};

const updateInvoice = (id, data) => {
  setMultipart(false);
  return client.patch(`/opd/counsellor/invoices/${id}`, data);
};

const consultantList = (catalogItemId, data) => {
  setMultipart(false);
  return client.post(
    `/opd/counsellor/catalog-item/${catalogItemId}/vendors`,
    data,
  );
};

const checkSlot = (params) => {
  return client.get(`/bookings/counsellor/available-slots`, { params });
};

const bookSlot = (id, data) => {
  setMultipart(false);
  return client.post(`/bookings/counsellor/${id}`, data);
};

const getBookingDetails = (id) => {
  return client.get(`/bookings/counsellor/${id}/details`);
};

// IPD APIs

const getIpdTransfers = (params) => {
  return client.get(`/opd/ipd-manager/transfer-list`, { params });
};

const addIpdAdmissionDocuments = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/${id}/admission-documents`, data);
};

const getIpdAdmissionFormPreview = (id, params) => {
  return client.get(`/ipd-users/${id}/admission-form`, { params });
};

const printIpdAdmissionForm = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/${id}/admission-form/pdf`, data);
};

const printIpdDischargeForm = (id, data = {}) => {
  setMultipart(false);
  return client.post(`/ipd-users/${id}/print/discharge`, data);
};

const getIpdBilling = (id) => {
  return client.get(`/billing/${id}`);
};

const createIpdBilling = (id, data) => {
  setMultipart(false);
  return client.post(`/opd/ipd-manager/${id}/invoice`, data);
};

const getIpdDashboard = (params) => {
  return client.get(`/ipd-users/dashboard`, { params });
};

const getIpdPatientSummary = (id, params) => {
  return client.get(`/ipd-users/${id}`, { params });
};

const getIpdDocumentTemplates = (params) => {
  return client.get(`/ipd-users/document-templates`, { params });
};

const addIpdDocumentTemplate = (data) => {
  setMultipart(false);
  return client.post(`/ipd-users/document-templates`, data);
};

const updateIpdDocumentTemplate = (id, data) => {
  setMultipart(false);
  return client.put(`/ipd-users/document-templates/${id}`, data);
};

const deleteIpdDocumentTemplate = (id) => {
  return client.delete(`/ipd-users/document-templates/${id}`);
};

const addIpdRoom = (data) => {
  setMultipart(false);
  return client.post(`/rooms`, data);
};

const updateIpdRoom = (id, data) => {
  setMultipart(false);
  return client.put(`/rooms/${id}`, data);
};

const deleteIpdRoom = (id) => {
  return client.delete(`/rooms/${id}`);
};

const getIpdRoomsStats = () => {
  return client.get(`/rooms/overview`);
};

const getIpdRoomBeds = (id, params) => {
  return client.get(`/rooms/${id}/beds`, { params });
};

const getIpdRoomVacantBeds = (id, params) => {
  return client.get(`/rooms/${id}/vacant-beds`, { params });
};

const getIpdRooms = (params) => {
  return client.get(`/rooms`, { params });
};

const getIpdRoomsCategories = (params) => {
  return client.get(`/rooms/categories`, { params });
};

const addIpdRoomsCategory = (data) => {
  setMultipart(false);
  return client.post(`/rooms/categories`, data);
};

const updateIpdRoomsCategory = (id, data) => {
  setMultipart(false);
  return client.put(`/rooms/categories/${id}`, data);
};

const deleteIpdRoomsCategory = (id) => {
  return client.delete(`/rooms/categories/${id}`);
};

const getRoomsHierarchy = () => {
  return client.get(`/rooms/hierarchy`);
};

const getRoomsHierarchyRooms = (params) => {
  return client.get(`/rooms/hierarchy/rooms`, { params });
};

const getIpdUsers = (params) => {
  return client.get(`/ipd-users`, { params });
};

const getIpdUser = (id) => {
  return client.get(`/ipd-users/${id}`);
};

const getIpdUserTestReports = (id, params) => {
  return client.get(`/ipd-users/${id}/test-reports`, { params });
};

const getIpdUserAdmissionDocuments = (id, params) => {
  return client.get(`/ipd-users/${id}/admission-documents`, { params });
};

const getIpdUserProgressNotes = (id, params) => {
  return client.get(`/ipd-users/${id}/progress-note-summary`, { params });
};

const allocateIpdUserRoom = (id, data) => {
  setMultipart(false);
  return client.put(`/ipd-users/${id}/assign-room-bed`, data);
};

const allocateIpdUserStaff = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/ipd-manager/${id}/staff-allocation`, data);
};

const getIpdUserDischargeSummary = (id) => {
  return client.get(`/ipd-users/${id}/discharge-summary`);
};

const updateIpdUserDischargeSummary = (id, data) => {
  setMultipart(false);
  return client.put(`/ipd-users/${id}/discharge`, data);
};

const updateIpdUserVitals = (id, data) => {
  setMultipart(false);
  return client.patch(`/ipd-users/${id}/vitals`, data);
};

const getIpdProcedures = (params) => {
  return client.get(`/ipd-procedures`, { params });
};

const getIpdProcedureById = (id) => {
  return client.get(`/ipd-procedures/${id}`);
};

const getIpdProcedurePricing = (id) => {
  return client.get(`/ipd-procedures/${id}/pricing`);
};

const getIpdUserStaffDetails = (id) => {
  return client.get(`/ipd-users/${id}/staff-details`);
};

const addIpdProcedure = (data) => {
  setMultipart(false);
  return client.post(`/ipd-procedures`, data);
};

const updateIpdProcedure = (id, data) => {
  setMultipart(false);
  return client.put(`/ipd-procedures/${id}`, data);
};

const deleteIpdProcedure = (id) => {
  return client.delete(`/ipd-procedures/${id}`);
};

const getIpdAgentKnowledgeBaseList = (params) => {
  return client.get("/ipd-users/ipd-manager/vendors/catalog-items", { params });
};

const getIpdAgentKnowledgeBase = (id) => {
  return client.get(`/ipd-users/ipd-manager/vendors/catalog-items/${id}`);
};

const getIpdBillings = (params) => {
  return client.get("/billing/list", { params });
};

const getIpdPatientSummaryList = (id, params) => {
  return client.get(`/ipd-users/${id}`, { params });
};

const getBillingById = (id) => {
  return client.get(`/billing/invoice/${id}`);
};

const printIpdBillingInvoice = (invoiceId, params) => {
  return client.get(`/billing/invoice/${invoiceId}/print`, { params });
};

const getInvoiceDetails = (id) => {
  return client.get(`/billing/unbilled/procedure/${id}`);
};

const createIpdManagerInvoice = (id, data) => {
  setMultipart(false);
  return client.post(`/opd/ipd-manager/${id}/invoice`, data);
};

const getVendors = (params) => {
  return client.get("/vendors", { params });
};

const getIpdStaff = (params) => {
  return client.get("/ipd-users/ipd-manager/staff-doctor-list", { params });
};

const getIpdStaffAllocation = (id, params) => {
  return client.get(`/ipd-users/ipd-manager/${id}/staff-allocation`, {
    params,
  });
};

const getIpdInventory = (params) => {
  return client.get("/store/inventory/department-inventory/ipd", { params });
};

const getIpdInventoryItems = () => {
  return client.get("/store/inventory/request-items/ipd");
};

const createIpdInventoryRequest = (data) => {
  setMultipart(false);
  return client.post("/store/inventory/inventory-requests/ipd", data);
};

const getIpdInventoryRequests = (params) => {
  return client.get("/store/inventory/inventory-requests/ipd", { params });
};

const updateIpdInventoryQuantity = (data) => {
  setMultipart(false);
  return client.post("/store/inventory/department-inventory/ipd/consume", data);
};

const itemSearch = (params) => {
  return client.get("/itemSearch", { params });
};

// Nurse Modules APIs

const getNurseDashboard = (params) => {
  return client.get(`/ipd-users/nurse/dashboard`, { params });
};

const getNursePatientList = (params) => {
  return client.get(`/ipd-users/nurse/patient-list`, { params });
};

const getNursePatientProcedure = (id) => {
  return client.get(`/ipd-users/nurse/${id}/procedures`);
};

const getProcedureDetails = (id) => {
  return client.get(`/ipd-users/nurse/procedures/${id}/details`);
};

const updateNursePatientProcedure = (IpdId, procID, data) => {
  setMultipart(false);
  return client.put(`/ipd-users/${IpdId}/procedures/${procID}`, data);
};

const createNurseClinicalNote = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/nurse/${id}/clinical-note`, data);
};

const getNurseClinicalNotes = (id, params) => {
  return client.get(`/ipd-users/nurse/${id}/clinical-notes`, { params });
};

const getConsumablesList = (params) => {
  return client.get(`/ipd-users/department-inventory`, { params });
};

const getConsumablesById = (id, params) => {
  return client.get(`/ipd-users/${id}/consumables-items`, { params });
};

const addConsumables = (id, data) => {
  setMultipart(false);
  return client.post(`/ipd-users/${id}/consumables-items`, data);
};

// Pharmacist APIs

const getPharmacistDashboard = (params) => {
  return client.get(`/pharmacy/dashboard`, { params });
};

const getProductsList = (params) => {
  return client.get(`/doctor/catalog-items/products`, { params });
};

const getOtcInvoices = (params) => {
  return client.get(`/pharmacy/invoices/otc/list`, { params });
};

const getOtcInvoiceById = (id) => {
  return client.get(`/pharmacy/invoices/otc/${id}`);
};

const createOtcInvoice = (data) => {
  setMultipart(false);
  return client.post(`/pharmacy/invoices/otc`, data);
};

const getIpdInvoiceList = (params) => {
  return client.get(`/pharmacy/invoices/medicine/list`, { params });
};

const getIpdInvoiceById = (id) => {
  return client.get(`/pharmacy/invoices/${id}`);
};

const createIpdInvoice = (data) => {
  setMultipart(false);
  return client.post(`/pharmacy/invoices/ipd-procedure`, data);
};

const getPatientsList = (params) => {
  return client.get("/pharmacy/ipd/patients", { params });
};

const getUnbilledMedicines = (id, source = "IPD") => {
  const pathSource = String(source).toLowerCase() === "opd" ? "opd" : "ipd";
  return client.get(
    `/pharmacy/${pathSource}/${id}/unbilled-medicine-procedures`,
  );
};

const getPharmacyDepartmentInventory = (params) => {
  return client.get("/pharmacy/department-inventory", { params });
};

const getPharmacyDepartmentInventoryRefillTransactions = (params) => {
  return client.get("/pharmacy/department-inventory/refill-transactions", {
    params,
  });
};

const getPharmacyDepartmentInventorySalesBreakdown = (id, params) => {
  return client.get(`/pharmacy/department-inventory/${id}/sales-breakdown`, {
    params,
  });
};

const getPharmacyDepartmentInventoryDetails = (id, params) => {
  return client.get(`/pharmacy/department-inventory/${id}/details`, { params });
};

const updatePharmacyDepartmentInventoryStockBulk = (data) => {
  setMultipart(false);
  return client.patch("/pharmacy/department-inventory/update-stock/bulk", data);
};

const updatePharmacyDepartmentInventoryStock = (data) => {
  setMultipart(false);
  return client.post("/pharmacy/department-inventory/update-stock", data);
};

const getPharmacyAppOrders = (params) => {
  return client.get("/pharmacy/app-orders", { params });
};

const getPharmacyAppOrder = (orderId) => {
  return client.get(`/pharmacy/app-orders/${orderId}`);
};

const updatePharmacyAppOrderStatus = (orderId, data) => {
  setMultipart(false);
  return client.put(`/pharmacy/app-orders/${orderId}/status`, data);
};

const getPharmacyAppOrderInvoice = (orderId, params) => {
  return client.get(`/pharmacy/app-orders/${orderId}/invoice`, { params });
};

// Radiologist APIs

const getRadiologistDashboard = (params) => {
  return client.get("/bookings/staff/dashboard", { params });
};

const getRadiologistBookings = (params) => {
  return client.get("/bookings/staff/assigned", { params });
};

const getRadiologistBooking = (id) => {
  return client.get(`/bookings/staff/assigned/${id}`);
};

const uploadRadiologistResult = (bookingId, testId, data) => {
  setMultipart(false);
  return client.put(
    `/bookings/staff/assigned/${bookingId}/items/${testId}/result-link`,
    data,
  );
};

const markRadiologistBookingItemAsDone = (bookingId, testId) => {
  setMultipart(false);
  return client.put(`/bookings/${bookingId}/items/${testId}/complete`);
};

const markRadiologistBookingAsDone = (bookingId) => {
  setMultipart(false);
  return client.put(`/bookings/${bookingId}/complete`);
};

// Lab Test APIs

const getLabTestDashboard = (params) => {
  return client.get("/bookings/lab-technician/dashboard", { params });
};

const getLabTestBookings = (params) => {
  return client.get("/bookings/lab-technician/assigned", { params });
};

const getLabTestBooking = (id) => {
  return client.get(`/bookings/lab-technician/assigned/${id}`);
};

const uploadLabTestResult = (bookingId, testId, data) => {
  setMultipart(false);
  return client.put(
    `/bookings/lab-technician/assigned/${bookingId}/items/${testId}/result-link`,
    data,
  );
};

const markLabTestBookingAsDone = (bookingId) => {
  setMultipart(false);
  return client.put(`/bookings/lab-technician/assigned/${bookingId}/complete`);
};

const getLabTestPickups = (params) => {
  return client.get("/bookings/lab-technician/pickup-list", { params });
};

const getLabTestPickupPartners = (params) => {
  return client.get("/bookings/lab-technician/pickup-partners", { params });
};

const assignPartnerToLabTestBooking = (id) => {
  setMultipart(false);
  return client.put(`/bookings/lab-technician/assigned/${id}/assign-partner`);
};

const removePartnerFromLabTestBooking = (id) => {
  return client.delete(
    `/bookings/lab-technician/assigned/${id}/remove-partner`,
  );
};

const updateLabTestBookingItemStatus = (bookingId, testId) => {
  setMultipart(false);
  return client.put(
    `/bookings/lab-technician/assigned/${bookingId}/items/${testId}/sample-collected/complete`,
  );
};

const autoAssignPartnerToLabTestBooking = (id) => {
  setMultipart(false);
  return client.put(
    `/bookings/lab-technician/assigned/${id}/assign-partner/auto`,
  );
};

// Therapist APIs

const getTherapistDashboard = (params) => {
  return client.get("/bookings/therapist/dashboard", { params });
};

const getTherapyBookings = (params) => {
  return client.get("/bookings/therapist/assigned", { params });
};

const getPartnerTherapyBookings = (params) => {
  return client.get("/partner/therapy-bookings", { params });
};

const claimPartnerTherapyBooking = (bookingId) => {
  setMultipart(false);
  return client.post(`/partner/therapy-bookings/${bookingId}/assign`);
};

const getPartnerLabBookings = (params) => {
  return client.get("/partner/lab-bookings", { params });
};

const claimPartnerLabBooking = (bookingId) => {
  setMultipart(false);
  return client.post(`/partner/lab-bookings/${bookingId}/assign`);
};

const getTherapyBookingsCalendar = (params) => {
  return client.get("/bookings/therapist/calendar", { params });
};

const getTherapyBookingById = (id, params) => {
  return client.get(`/bookings/therapist/opd/${id}/patient-summary`, {
    params,
  });
};

const getTherapistIpdSessions = (params) => {
  return client.get("/bookings/therapist/ipd-sessions", { params });
};

const getTherapyIpdPatientSummary = (id) => {
  return client.get(`/ipd-users/${id}`);
};

const getTherapyIpdPatientReports = (params) => {
  return client.get(`/bookings/therapist/patient-therapy-sessions`, { params });
};

const getTherapyBookingPreassesment = (id) => {
  return client.get(`/bookings/therapist/opd/${id}/pre-assessment`);
};

const getTherapyBookingPrescription = (id) => {
  return client.get(`/bookings/therapist/prescriptions/${id}`);
};

const getTherapyBookingDhp = (params) => {
  return client.get(`/user/digital-health-profile/report`, { params });
};

const updateTherapySession = (bookingId, serviceId, data) => {
  setMultipart(false);
  return client.patch(
    `/bookings/therapist/assigned/${encodeURIComponent(bookingId)}/items/${encodeURIComponent(serviceId)}/session`,
    data,
  );
};

const getTherapistInventory = (params) => {
  return client.get("/store/inventory/department-inventory/therapist", {
    params,
  });
};

const getTherapistInventoryItems = () => {
  return client.get("/store/inventory/request-items/therapist");
};

const createTherapistInventoryRequest = (data) => {
  setMultipart(false);
  return client.post("/store/inventory/inventory-requests/therapist", data);
};

const getTherapistInventoryRequests = (params) => {
  return client.get("/store/inventory/inventory-requests/therapist", {
    params,
  });
};

const updateTherapistInventoryQuantity = (data) => {
  setMultipart(false);
  return client.post(
    "/store/inventory/department-inventory/therapist/consume",
    data,
  );
};

// Yoga Instructor APIs

const getYogaInstructorDashboard = (params) => {
  return client.get("/bookings/yoga-instructor/dashboard", { params });
};

const getYogaBusinessDashboard = (params) => {
  return client.get("/staff/yoga-business/dashboard", { params });
};

const getYogaInstructorAssignedBookings = (params) => {
  return client.get("/bookings/yoga-instructor/assigned", { params });
};

const getYogaPaymentInvoices = (params) => {
  return client.get("/bookings/yoga-instructor/invoices", { params });
};

const getYogaPaymentInvoiceById = (invoiceId) => {
  return client.get(`/bookings/yoga-instructor/invoices/${invoiceId}`);
};

const printYogaPaymentInvoice = (invoiceId, params) => {
  return client.get(`/bookings/yoga-instructor/invoices/${invoiceId}/print`, {
    params,
  });
};

const getYogaBookingsCalendarView = (params) => {
  return client.get("/bookings/yoga-instructor/calendar", { params });
};

const getYogaInstructorPatientSummary = (opdId, params) => {
  return client.get(`/bookings/yoga-instructor/opd/${opdId}/patient-summary`, {
    params,
  });
};

const getYogaPatientPrescription = (id) => {
  return client.get(`/bookings/yoga-instructor/prescriptions/${id}`);
};

const getYogaPatientDhp = (params) => {
  return client.get(`/user/digital-health-profile/report`, { params });
};

const getYogaIpdList = (params) => {
  return client.get(`/bookings/yoga-instructor/ipd-sessions`, { params });
};

const getYogaIpdPatientSummary = (id) => {
  return client.get(`/ipd-users/${id}`);
};

const getYogaIpdPatientDhp = (params) => {
  return client.get(`/user/digital-health-profile/report`, { params });
};

const getYogaIpdPatientProcedures = (id, params) => {
  return client.get(`/ipd-users/yoga-instructor/${id}/yoga-sessions`, {
    params,
  });
};

const getYogaIpdPatientDiet = (ipdCaseId) => {
  return client.get(`/ipd-users/yoga-instructor/${ipdCaseId}/diet`);
};

const completeYogaIpdSession = (ipdCaseId, body) => {
  return client.patch(
    `/ipd-users/yoga-instructor/${ipdCaseId}/yoga-sessions/complete`,
    body,
  );
};

const getYogaOfferings = (params) => {
  return client.get("/staff/yoga-offerings", { params });
};

/** CRM + hospital yoga master inventory for session asana picker (yoga instructor). */
const getYogaAsanas = (params) => {
  return client.get("/yoga", {
    params: { scope: "picker", status: "active", ...params },
  });
};

const createYogaOffering = (data) => {
  setMultipart(false);
  return client.post("/staff/yoga-offerings", data);
};

const getYogaOfferingById = (offeringId) => {
  return client.get(`/staff/yoga-offerings/${offeringId}`);
};

const updateYogaOffering = (offeringId, data) => {
  setMultipart(false);
  return client.put(`/staff/yoga-offerings/${offeringId}`, data);
};

const deleteYogaOffering = (offeringId) => {
  return client.delete(`/staff/yoga-offerings/${offeringId}`);
};

const getYogaRecordedOfferingDetails = (offeringId) => {
  return client.get(`/staff/yoga-offerings/${offeringId}/recorded-details`);
};

const getYogaOneToOneOfferingDetails = (offeringId) => {
  return client.get(`/staff/yoga-offerings/${offeringId}/one-to-one-details`);
};

const getYogaGroupOfferingDetails = (offeringId) => {
  return client.get(
    `/staff/yoga-offerings/${offeringId}/group-session-details`,
  );
};

const getYogaOfferingBookings = (offeringId, params) => {
  return client.get(`/staff/yoga-offerings/${offeringId}/bookings`, {
    params,
  });
};

const getCatalogPolicies = (params) => {
  return client.get("/catalog-policies", { params });
};

// Billing Manager APIs

const getBillingManagerDashboard = (params) => {
  return client.get("/billing/manager/dashboard", { params });
};

const getBillingManagerProviders = (params) => {
  return client.get("/billing/manager/providers", { params });
};

const getServiceProviders = (params) => {
  return client.get("/service-providers", { params });
};

const getBillingManagerServiceProviderSettlements = (
  serviceProviderId,
  params,
) => {
  return client.get(
    `/billing/manager/service-provider/${serviceProviderId}/settlements`,
    { params },
  );
};

const settleBillingManagerMargin = (marginId, data) => {
  setMultipart(false);
  return client.patch(`/billing/manager/margin/${marginId}/settle`, data);
};

const getMarginInvoicePdf = (marginInvoiceId) => {
  return client.get(`/margin-invoices/${marginInvoiceId}/pdf`);
};

const getBillingManagerProvider = (id) => {
  return client.get(`/billing/manager/providers/${id}`);
};

const getBillingManagerInvoices = (params) => {
  return client.get("/billing/manager/invoices", { params });
};

const getBillingManagerInvoice = (id) => {
  return client.get(`/billing/manager/invoices/${id}`);
};

const getBillingManagerRefunds = (params) => {
  return client.get("/billing/manager/refunds", { params });
};

const getBillingManagerRefund = (id) => {
  return client.get(`/billing/manager/refunds/${id}`);
};

const createBillingManagerRefund = (id, data) => {
  setMultipart(false);
  return client.patch(`/billing/invoice/${id}/refund`, data);
};

const printBillingManagerInvoice = (invoiceId, params) => {
  return client.get(`/billing/manager/invoices/${invoiceId}/print`, {
    params,
  });
};

const payBillingManagerInvoice = (id, data) => {
  setMultipart(false);
  return client.patch(`/billing/invoice/${id}/pay`, data);
};

const getBillingManagerPaymentMethods = () => {
  return client.get(`/billing/manager/payment-methods`);
};

const createBillingManagerInvoiceQrCode = (invoiceId) => {
  setMultipart(false);
  return client.post(`/billing/manager/invoices/${invoiceId}/qr-code`);
};

const getBillingManagerInvoiceQrCodeStatus = (invoiceId, qrCodeId) => {
  return client.get(
    `/billing/manager/invoices/${invoiceId}/qr-code/${qrCodeId}/status`,
  );
};

// Dietician APIs

const getDieticianDashboard = (params) => {
  return client.get("/dietician/dashboard", { params });
};

const getDieticianAppointments = (params) => {
  return client.get("/dietician/appointments", { params });
};

const getDieticianCalendarAppointments = (params) => {
  return client.get("/dietician/appointments/calendar", { params });
};

const getDieticianAppointment = (params) => {
  return client.get("/dietician/patients/summary", { params });
};

const getDieticianPlans = (params) => {
  return client.get("/dietician/diets", { params });
};

const addDieticianPlan = (data) => {
  setMultipart(false);
  return client.post("/dietician/diets", data);
};

const getDieticianPlan = (id) => {
  return client.get(`/dietician/diets/${id}/full`);
};

const updateDieticianPlan = (id, data) => {
  setMultipart(false);
  return client.put(`/dietician/diets/${id}`, data);
};

const deleteDieticianPlan = (id) => {
  return client.delete(`/dietician/diets/${id}`);
};

const getDieticianIPDs = (params) => {
  return client.get("/dietician/ipd/patients", { params });
};

const addDieticianPrescription = (id, data) => {
  setMultipart(false);
  return client.put(`/dietician/opd/${id}/daily-meal-structure`, data);
};

const getDieticianPrescription = (id) => {
  return client.get(`/dietician/opd/${id}/prescription-summary`);
};

const getDieticianProgressNotes = (id, params) => {
  return client.get(`/dietician/opd/${id}/progress-notes`, { params });
};

const addDieticianProgressNote = (id, data) => {
  setMultipart(false);
  return client.post(`/dietician/opd/${id}/progress-notes`, data);
};

const getNurseIpdDietPlan = (ipdCaseId) => {
  return client.get(`/ipd-users/dietician/${ipdCaseId}/diet-plan`);
};

const updateDieticianIpdDietPlan = (ipdCaseId, data) => {
  setMultipart(false);
  return client.patch(`/ipd-users/dietician/${ipdCaseId}/diet-plan`, data);
};

const getDieticianIpdAppointments = (params) => {
  return client.get(`/dietician/ipd/patients`, { params });
};

const getDieticianIpdPatientDetails = (ipdId) => {
  return client.get(`/dietician/ipd/patients/${ipdId}`);
};

const addDieticianIpdProgressNote = (ipdId, data) => {
  setMultipart(false);
  return client.post(`/dietician/ipd/patients/${ipdId}/progress-notes`, data);
};

/** Staff Manager*/
const getStaffManagerList = (params) => {
  return client.get("/staff/list", { params });
};

const getStaffStats = () => {
  return client.get("/staff/stats");
};

const registerStaff = (data) => {
  setMultipart(false);
  return client.post("/staff/register", data);
};

const getStaffManagerDetails = (staffId, params) => {
  return client.get(`/staff/${staffId}/details`, { params });
};

const updateStaffShift = (staffId, data) => {
  return client.put(`/staff/${staffId}/shift`, data);
};

const updateStaffRole = (staffId, data) => {
  return client.put(`/staff/${staffId}/role`, data);
};

const updateStaffDepartment = (staffId, data) => {
  return client.put(`/staff/${staffId}/department`, data);
};

const updateStaffAvailability = (staffId, data) => {
  return client.put(`/staff/${staffId}/availability`, data);
};

const updateStaffHospitalAccountStatus = (staffId, data) => {
  return client.put(`/staff/${staffId}/hospital-account-status`, data);
};

const updateStaffHospitalCredentials = (staffId, data) => {
  return client.put(`/staff/${staffId}/hospital-credentials`, data);
};

const getStaffSlots = (staffId) => {
  return client.get(`/staff-manager/staff/${staffId}/slots`);
};

const updateStaffSlots = (staffId, data) => {
  return client.put(`/staff-manager/staff/${staffId}/slots`, data);
};

const updateStaff = (staffId, data) => {
  setMultipart(false);
  return client.put(`/staff/${staffId}`, data);
};

const getServices = (params) => {
  return client.get("/services", { params });
};

const getStaffServiceItems = (staffId) => {
  return client.get(`/staff/${staffId}/service-items`);
};

const addStaffServiceItem = (staffId, data) => {
  setMultipart(false);
  return client.post(`/staff/${staffId}/service-items`, data);
};

const updateStaffServiceItem = (staffId, assignmentId, data) => {
  setMultipart(false);
  return client.put(`/staff/${staffId}/service-items/${assignmentId}`, data);
};

const deleteStaffServiceItem = (staffId, assignmentId) => {
  return client.delete(`/staff/${staffId}/service-items/${assignmentId}`);
};

const getAvailableStaffRoles = () => {
  return client.get("/auth/hospital/me/roles");
};

// Store Manager APIs

const getStoreManagerDashboard = (params) => {
  return client.get("/store/inventory/dashboard", { params });
};

const getStoreManagerInventory = (params) => {
  return client.get("/store/inventory/master", { params });
};

const updateStoreManagerInventoryStock = (data) => {
  setMultipart(false);
  return client.post(`/store/inventory/update-stock`, data);
};

const bulkUpdateStoreManagerInventoryStock = (data) => {
  setMultipart(false);
  return client.post(`/store/inventory/update-stock/bulk`, data);
};

const getStoreManagerInventoryRefillTransactions = (id, params) => {
  return client.get(`/store/inventory/items/${id}/refill-transactions`, {
    params,
  });
};

const getStoreManagerInventoryProduct = (id, params) => {
  return client.get(`/store/inventory/items/${id}`, { params });
};

const getStoreManagerInventoryDistribution = (id, params) => {
  return client.get(`/store/inventory/items/${id}/distribution`, {
    params,
  });
};

const getStoreManagerInventoryAssignmentHistory = (id, params) => {
  return client.get(`/store/inventory/items/${id}/assignment-history`, {
    params,
  });
};

const getStoreManagerInventoryRefillRequests = (params) => {
  return client.get("/store/inventory/inventory-requests", { params });
};

const getStoreManagerInventoryRefillRequest = (id) => {
  return client.get(`/store/inventory/inventory-requests/${id}`);
};

const completeStoreManagerInventoryRefillRequest = (id, data) => {
  setMultipart(false);
  return client.post(
    `/store/inventory/inventory-requests/${id}/complete`,
    data,
  );
};

const rejectStoreManagerInventoryRefillRequest = (id, data) => {
  setMultipart(false);
  return client.post(`/store/inventory/inventory-requests/${id}/reject`, data);
};

const getStoreManagerProducts = (params) => {
  return client.get("/hospital-products", { params });
};

const exportStoreManagerProductsCsv = () => {
  return client.get("/hospital-products/export-csv");
};

const addStoreManagerProduct = (data) => {
  setMultipart(false);
  return client.post("/hospital-products", data);
};

const updateStoreManagerProduct = (id, data) => {
  setMultipart(false);
  return client.put(`/hospital-products/${id}`, data);
};

const deleteStoreManagerProduct = (id) => {
  return client.delete(`/hospital-products/${id}`);
};

const getStoreManagerProduct = (id) => {
  return client.get(`/hospital-products/${id}`);
};

const checkBulkStoreManagerProducts = (data) => {
  setMultipart(false);
  return client.post("/hospital-products/bulk-upload", data);
};

const uploadBulkStoreManagerProducts = (data) => {
  setMultipart(false);
  return client.post("/hospital-products/bulk-insert", data);
};

const getStoreManagerServices = (params) => {
  return client.get("/hospital-services", { params });
};

const exportStoreManagerServicesCsv = () => {
  return client.get("/hospital-services/export-csv");
};

const addStoreManagerService = (data) => {
  setMultipart(false);
  return client.post("/hospital-services", data);
};

const updateStoreManagerService = (id, data) => {
  setMultipart(false);
  return client.put(`/hospital-services/${id}`, data);
};

const deleteStoreManagerService = (id) => {
  return client.delete(`/hospital-services/${id}`);
};

const getStoreManagerService = (id) => {
  return client.get(`/hospital-services/${id}`);
};

const checkBulkStoreManagerServices = (data) => {
  setMultipart(false);
  return client.post("/hospital-services/bulk-upload", data);
};

const uploadBulkStoreManagerServices = (data) => {
  setMultipart(false);
  return client.post("/hospital-services/bulk-insert", data);
};

// Hospital Admin APIs
const getHospitalAdminDashboard = (hospitalId, params) => {
  return client.get(`/hospitals/${hospitalId}/admin-dashboard`, { params });
};

const getHospitalStakeholders = (role) => {
  return client.get("/hospital-admin/stakeholders", { params: { role } });
};

const getHospitalStakeholdersOverview = () => {
  return client.get("/hospital-admin/stakeholders/overview");
};

const getPublicServicesMetadata = () => {
  return client.get("/public/services/all");
};

const getAdminMinutesBalance = (params) => {
  return client.get("/hospital-mint/admin/minutes-balance", { params });
};

const getAdminAllocationOverview = (params) => {
  return client.get("/hospital-mint/admin/allocation/overview", { params });
};

const getAdminAllocationDoctors = (params) => {
  return client.get("/hospital-mint/admin/allocation/doctors", { params });
};

const postAdminAllocateMinutes = (data) => {
  setMultipart(false);
  return client.post("/hospital-mint/admin/allocate", data);
};

const postAdminPurchaseInitiate = (data) => {
  setMultipart(false);
  return client.post("/hospital-mint/admin/purchase/initiate", data);
};

const getAdminPurchaseStatus = (params) => {
  return client.get("/hospital-mint/admin/purchase/status", { params });
};

const getDoctorMinutesBalance = () => {
  return client.get("/hospital-mint/doctor/minutes-balance");
};

const getDoctorAllocationHistory = (params) => {
  return client.get("/hospital-mint/doctor/allocation-history", { params });
};

const getAdminDoctorAllocationUsage = (staffId, params) => {
  return client.get(
    `/hospital-mint/admin/allocation/doctors/${encodeURIComponent(staffId)}/usage`,
    { params },
  );
};

const getAdminDoctorAllocationHistory = (staffId, params) => {
  return client.get(
    `/hospital-mint/admin/allocation/doctors/${encodeURIComponent(staffId)}/allocation-history`,
    { params },
  );
};

export default {
  // Login API
  login,
  hospitalResetPassword,
  requestPasswordReset,
  verifyPasswordReset,
  confirmPasswordReset,

  //Profile APIs
  getStaffMeProfile,
  postClientIssueReport,
  getAvailability,
  updateAvailability,

  uploadSingleFile,
  uploadMultipleFiles,
  getArrayByKey,
  getOpdAppointments,
  getOpdPatients,
  getReceptionistDashboard,
  getReceptionistAppointmentBehavior,
  cancelAppointment,
  markAsNoShowAppointment,
  addPatient,
  updatePatient,
  getStaffList,
  getCalendarSlots,
  getStaffDetails,
  getOpdReceptionistAvailableSlots,
  getOpdAssigneeSlotTypePricing,
  addAppointment,
  createReceptionistOpdInvoice,
  printReceptionistInvoice,
  getReceptionistPaymentMethods,
  getCounsellorPaymentMethods,
  createReceptionistOpdQrCode,
  getReceptionistOpdQrCodeStatus,
  createCounsellorInvoiceQrCode,
  getCounsellorInvoiceQrCodeStatus,
  rescheduleAppointment,
  getAssessments,
  getAssessmentView,
  editAssessment,
  getAssessmentStats,
  getBillings,
  getDoctorStatus,
  getDoctorStaffSchedule,
  getFollowUpList,
  getFollowUpStats,
  confirmFollowUp,
  cancelFollowUp,
  getFollowUpSlots,
  getPatient,
  getPatientAppointments,
  getPatientInvoices,
  getDoctorDashboard,
  getDoctorAppointments,
  getDoctorPrescriptions,
  getDoctorPrescription,
  listAvatarDraftPrescriptions,
  getAvatarDraftPrescription,
  reviewAvatarDraftPrescription,
  saveAvatarDraftLineItems,
  followUpAvatarDraftPrescription,
  printDoctorPrescription,
  getDoctorAppointmentCalendar,
  rescheduleFollowUp,
  getDoctorAssessment,
  getOpdAppointment,
  addDoctorOpdPrescription,
  addDoctorIpdPrescription,
  editDoctorIpdPrescription,
  getAllProcedures,
  financeDashboard,
  getRecentTransactions,
  productItemsList,
  serviceItemsList,
  getIpdPatients,
  getPatientDetails,
  getPatientReports,
  getProcedureData,
  getClinicalNotes,
  addOrEditProcedure,
  createDoctorNote,
  createNurseNote,
  getSnapshotData,
  getDhpData,
  getDhpKeyVitals,
  getMesh,
  getMeshPayload,
  getPatientSystemic,
  getOrganInsights,
  getDietList,
  startDischargeFromDoctor,

  //Counsellor APIs
  getCounsellorDashboard,
  getCounsellorList,
  getPatientSummary,
  cancelAdmission,
  moveToIpdManager,
  getItemInfo,
  createInvoice,
  getCounsellorCatalogItemPricing,
  getInvoiceList,
  getInvoiceById,
  updateInvoice,
  printCounsellorInvoice,
  consultantList,
  checkSlot,
  bookSlot,
  getBookingDetails,

  // IPD APIs
  getIpdTransfers,
  getIpdDashboard,
  getIpdPatientSummary,
  getIpdDocumentTemplates,
  addIpdDocumentTemplate,
  updateIpdDocumentTemplate,
  deleteIpdDocumentTemplate,
  addIpdRoom,
  updateIpdRoom,
  deleteIpdRoom,
  getIpdRoomsStats,
  getIpdRoomBeds,
  getIpdRoomVacantBeds,
  getIpdRooms,
  getIpdRoomsCategories,
  addIpdRoomsCategory,
  updateIpdRoomsCategory,
  deleteIpdRoomsCategory,
  getRoomsHierarchy,
  getRoomsHierarchyRooms,
  getIpdUsers,
  getIpdProcedures,
  getIpdProcedureById,
  getIpdProcedurePricing,
  getIpdUserStaffDetails,
  addIpdProcedure,
  updateIpdProcedure,
  deleteIpdProcedure,
  getIpdAgentKnowledgeBaseList,
  getIpdAgentKnowledgeBase,
  getIpdBillings,
  getIpdPatientSummaryList,
  getBillingById,
  printIpdBillingInvoice,
  getInvoiceDetails,
  createIpdManagerInvoice,
  getVendors,
  getIpdUser,
  getIpdUserTestReports,
  getIpdUserAdmissionDocuments,
  getIpdUserProgressNotes,
  getIpdUserDischargeSummary,
  updateIpdUserDischargeSummary,
  updateIpdUserVitals,
  getIpdStaff,
  getIpdStaffAllocation,
  allocateIpdUserRoom,
  allocateIpdUserStaff,
  getIpdBilling,
  createIpdBilling,
  addIpdAdmissionDocuments,
  getIpdAdmissionFormPreview,
  printIpdAdmissionForm,
  printIpdDischargeForm,
  getIpdInventory,
  getIpdInventoryItems,
  createIpdInventoryRequest,
  getIpdInventoryRequests,
  updateIpdInventoryQuantity,
  itemSearch,

  // Nurse APIs
  getNurseDashboard,
  getNursePatientList,
  getNursePatientProcedure,
  getProcedureDetails,
  updateNursePatientProcedure,
  createNurseClinicalNote,
  getNurseClinicalNotes,
  getConsumablesList,
  getConsumablesById,
  addConsumables,

  // Pharmacist APIs
  getPharmacistDashboard,
  getProductsList,
  getOtcInvoices,
  getOtcInvoiceById,
  createOtcInvoice,
  getIpdInvoiceList,
  getIpdInvoiceById,
  createIpdInvoice,
  getPatientsList,
  getUnbilledMedicines,
  getPharmacyDepartmentInventory,
  getPharmacyDepartmentInventoryRefillTransactions,
  getPharmacyDepartmentInventorySalesBreakdown,
  getPharmacyDepartmentInventoryDetails,
  updatePharmacyDepartmentInventoryStockBulk,
  updatePharmacyDepartmentInventoryStock,
  getPharmacyAppOrders,
  getPharmacyAppOrder,
  updatePharmacyAppOrderStatus,
  getPharmacyAppOrderInvoice,

  // Radiologist APIs
  getRadiologistDashboard,
  getRadiologistBookings,
  getRadiologistBooking,
  uploadRadiologistResult,
  markRadiologistBookingItemAsDone,
  markRadiologistBookingAsDone,

  // Lab Test APIs
  getLabTestDashboard,
  getLabTestBookings,
  getLabTestBooking,
  uploadLabTestResult,
  markLabTestBookingAsDone,
  getLabTestPickups,
  getLabTestPickupPartners,
  assignPartnerToLabTestBooking,
  removePartnerFromLabTestBooking,
  updateLabTestBookingItemStatus,
  autoAssignPartnerToLabTestBooking,

  // Therapist APIs
  getTherapistDashboard,
  getTherapyBookings,
  getPartnerTherapyBookings,
  claimPartnerTherapyBooking,
  getPartnerLabBookings,
  claimPartnerLabBooking,
  getTherapyBookingsCalendar,
  getTherapyBookingById,
  getTherapistIpdSessions,
  getTherapyIpdPatientSummary,
  getTherapyIpdPatientReports,
  getTherapyBookingPreassesment,
  getTherapyBookingPrescription,
  getTherapyBookingDhp,
  updateTherapySession,
  getTherapistInventory,
  getTherapistInventoryItems,
  createTherapistInventoryRequest,
  getTherapistInventoryRequests,
  updateTherapistInventoryQuantity,

  // Yoga Instructor APIs
  getYogaInstructorDashboard,
  getYogaBusinessDashboard,
  getYogaInstructorAssignedBookings,
  getYogaPaymentInvoices,
  getYogaPaymentInvoiceById,
  printYogaPaymentInvoice,
  getYogaBookingsCalendarView,
  getYogaInstructorPatientSummary,
  getYogaPatientPrescription,
  getYogaPatientDhp,
  getYogaIpdList,
  getYogaIpdPatientSummary,
  getYogaIpdPatientDhp,
  getYogaIpdPatientProcedures,
  getYogaIpdPatientDiet,
  completeYogaIpdSession,
  getYogaOfferings,
  getYogaAsanas,
  createYogaOffering,
  getYogaOfferingById,
  updateYogaOffering,
  deleteYogaOffering,
  getYogaRecordedOfferingDetails,
  getYogaOneToOneOfferingDetails,
  getYogaGroupOfferingDetails,
  getYogaOfferingBookings,
  getCatalogPolicies,

  // Billing Manager APIs
  getBillingManagerDashboard,
  getBillingManagerProviders,
  getServiceProviders,
  getBillingManagerProvider,
  getBillingManagerServiceProviderSettlements,
  settleBillingManagerMargin,
  getMarginInvoicePdf,
  getBillingManagerInvoices,
  getBillingManagerInvoice,
  getBillingManagerRefunds,
  getBillingManagerRefund,
  createBillingManagerRefund,
  printBillingManagerInvoice,
  payBillingManagerInvoice,
  getBillingManagerPaymentMethods,
  createBillingManagerInvoiceQrCode,
  getBillingManagerInvoiceQrCodeStatus,

  // Dietician APIs
  getDieticianDashboard,
  getDieticianAppointments,
  getDieticianCalendarAppointments,
  getDieticianAppointment,
  getDieticianPlans,
  addDieticianPlan,
  getDieticianPlan,
  updateDieticianPlan,
  deleteDieticianPlan,
  getDieticianIPDs,
  addDieticianPrescription,
  getDieticianPrescription,
  getDieticianProgressNotes,
  addDieticianProgressNote,
  getNurseIpdDietPlan,
  updateDieticianIpdDietPlan,
  getDieticianIpdAppointments,
  getDieticianIpdPatientDetails,
  addDieticianIpdProgressNote,

  // Staff Manager APIs
  getStaffManagerList,
  getStaffStats,
  registerStaff,
  getStaffManagerDetails,
  updateStaffShift,
  updateStaffRole,
  updateStaffDepartment,
  updateStaffAvailability,
  updateStaffHospitalAccountStatus,
  updateStaffHospitalCredentials,
  getStaffSlots,
  updateStaffSlots,
  updateStaff,
  getServices,
  getStaffServiceItems,
  addStaffServiceItem,
  updateStaffServiceItem,
  deleteStaffServiceItem,
  getAvailableStaffRoles,

  // Store Manager APIs
  getStoreManagerDashboard,
  getStoreManagerInventory,
  updateStoreManagerInventoryStock,
  bulkUpdateStoreManagerInventoryStock,
  getStoreManagerInventoryRefillTransactions,
  getStoreManagerInventoryProduct,
  getStoreManagerInventoryDistribution,
  getStoreManagerInventoryAssignmentHistory,
  getStoreManagerInventoryRefillRequests,
  getStoreManagerInventoryRefillRequest,
  completeStoreManagerInventoryRefillRequest,
  rejectStoreManagerInventoryRefillRequest,
  getStoreManagerProducts,
  exportStoreManagerProductsCsv,
  addStoreManagerProduct,
  updateStoreManagerProduct,
  deleteStoreManagerProduct,
  getStoreManagerProduct,
  checkBulkStoreManagerProducts,
  uploadBulkStoreManagerProducts,
  getStoreManagerServices,
  exportStoreManagerServicesCsv,
  addStoreManagerService,
  updateStoreManagerService,
  deleteStoreManagerService,
  getStoreManagerService,
  checkBulkStoreManagerServices,
  uploadBulkStoreManagerServices,

  // Hospital Admin APIs
  getHospitalAdminDashboard,
  getHospitalStakeholders,
  getHospitalStakeholdersByRole: getHospitalStakeholders,
  getHospitalStakeholdersOverview,
  getPublicServicesMetadata,
  getAdminMinutesBalance,
  getAdminAllocationOverview,
  getAdminAllocationDoctors,
  postAdminAllocateMinutes,
  postAdminPurchaseInitiate,
  getAdminPurchaseStatus,
  getDoctorMinutesBalance,
  getDoctorAllocationHistory,
  getAdminDoctorAllocationUsage,
  getAdminDoctorAllocationHistory,
};
